import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { FaXTwitter } from "react-icons/fa6";
import {
  FaFacebookSquare,
  FaGithubSquare,
  FaInstagram,
  FaWhatsapp,
  FaMobile,
  FaEnvelope,
  FaMapMarkerAlt,
  FaDiscord,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";

const SOCIALS = [
  { Icon: FaFacebookSquare, label: "Facebook", url: "https://www.facebook.com/profile.php?id=100064823795648" },
  { Icon: FaInstagram, label: "Instagram", url: "https://instagram.com" },
  { Icon: FaXTwitter, label: "Twitter", url: "https://twitter.com" },
  { Icon: FaGithubSquare, label: "GitHub", url: "https://github.com" },
  { Icon: FaDiscord, label: "Discord", url: "https://discord.gg/Y2WYtuSN" },
  { Icon: FaWhatsapp, label: "WhatsApp", url: "https://wa.me/23672965555" },
];

const Footer = ({ id }) => {
  const { t } = useTranslation();

  // Get sections and links from translation
  const sections = t("footer.sections", { returnObjects: true });
  const sectionsLinks = t("footer.sectionsLinks", { returnObjects: true });

  // Scroll to top handler
  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer
      id={id}
      className="max-w-[1200px] mx-auto py-16 px-4 text-gray-300 2xl:max-w-[1536px] 3xl:max-w-[1920px] 4xl:max-w-[2560px] 5xl:max-w-[3840px]"
    >
      {/* Main grid */}
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Left section: Logo & Description */}
        <div data-aos="fade-right">
          <h1 className="w-full text-2xl font-bold text-[#00df9a] 2xl:text-4xl 3xl:text-5xl 4xl:text-5xl 5xl:text-5xl">
            <Link to="/" onClick={handleLinkClick}>
              {t("footer.logo")}
            </Link>
          </h1>
          <div className="flex flex-col gap-4 py-4 2xl:text-lg 3xl:text-xl 4xl:text-xl 5xl:text-xl">
            <a
              href="tel:+23672965555"
              className="flex items-center gap-2 hover:underline"
              aria-label={t("footer.contact.phone")}
            >
              <FaMobile className="text-gray-200" />
              +236 72 96 55 55
            </a>
            <a
              href="mailto:contact@decintech.com"
              className="flex items-center gap-2 hover:underline"
              aria-label={t("footer.contact.email")}
            >
              <FaEnvelope className="text-gray-200" />
              contact@decintech.com
            </a>
            <a
              href="https://maps.app.goo.gl/u7yxgVFJr7LyzP9u5"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:underline"
              aria-label={t("footer.contact.address")}
            >
              <FaMapMarkerAlt className="text-red-600" />
              {t("footer.contact.address")}
            </a>
          </div>
        </div>

        {/* Right section: Navigation */}
        <div className="lg:col-span-2 flex flex-col justify-between">
          <div
            className="grid grid-cols-2 sm:grid-cols-4 gap-8 mt-6 lg:mt-0"
            data-aos="fade-left"
          >
            {sections.map((section, index) => (
              <div key={index}>
                <h2 className="font-medium text-[#00df9a] mb-4 2xl:text-lg 3xl:text-xl 4xl:text-xl 5xl:text-xl">
                  {section.title}
                </h2>
                <ul>
                  {section.items.map((item, idx) => (
                    <li
                      key={idx}
                      className="py-2 text-sm 2xl:text-base 3xl:text-lg 4xl:text-lg 5xl:text-lg"
                    >
                      <Link
                        to={sectionsLinks[index][idx]}
                        onClick={handleLinkClick}
                      >
                        {item}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Social Media Icons */}
      <div
        className="
          flex justify-between items-center
          mt-8
          md:w-[75%] 
          mx-auto
          lg:mx-0
          lg:mt-6
          lg:justify-start
          lg:gap-6
          order-last
          lg:order-none
        "
        data-aos="fade-up"
        role="list"
      >
        {SOCIALS.map(({ Icon, label, url }, index) => (
          <a
            key={index}
            href={url}
            aria-label={t(`footer.socials.${label}`)}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[#00df9a] transition-colors"
          >
            <Icon
              size={30}
              className="2xl:w-8 2xl:h-8 3xl:w-10 3xl:h-10 4xl:w-12 4xl:h-12 5xl:w-12 5xl:h-12"
              aria-hidden="true"
            />
          </a>
        ))}
      </div>

      {/* Copyright */}
      <div className="mt-12 border-t border-[#00df9a] pt-6 flex flex-col items-center text-center text-xs sm:text-sm 2xl:text-base 3xl:text-lg 4xl:text-lg 5xl:text-lg">
        <p className="mb-2 text-sm">
          &copy; {new Date().getFullYear()} {t("footer.logo")}.{" "}
          {t("footer.copyright")}
        </p>
      </div>
    </footer>
  );
};

Footer.propTypes = {
  id: PropTypes.string,
};

export default Footer;
